import React, { useState, useEffect } from 'react';
import {
	InsuranceReport,
	InsuranceReportState,
	InsuranceReportStateError,
	useInsuranceReportUpdateMutation,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import Text from '../../../../Components/Base/Text/Text';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import NCZIReferenceSearchSelectField from '../../../Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import { NCZI_OID } from '../../../Nczi/Enums/ncziOid.enum';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import InsuranceOperationSelectField from '../InsuranceOperationSelect/InsuranceOperationSelectField';
import NumberField from '../../../../Components/Form/components/NumberField';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useNCZIReference from '../../../Nczi/Hooks/useNCZIReference';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { useEffectAsync } from '../../../../helpers';
import InputField from '../../../../Components/Form/components/InputField';
import useFilters from '../../../Base/Hooks/useFilters';
import { Moment } from 'moment';
import DateField from '../../../../Components/Form/components/DateField';
import SelectField from '../../../../Components/Form/components/SelectField';
import DoctorSelectField from '../../../../Components/Form/components/DoctorSelectField';

interface InsuranceReportUpdateValues {
	diagnosis: SelectValue;
	reportedAt: Moment;
	doctor: SelectValue;
	operation: {
		label: string;
		value: string;
	};
	count: string;
	points: string;
	birthNumber: string;
	state: {
		label: string;
		value: InsuranceReportState;
	};
	stateError: {
		label: string;
		value: InsuranceReportStateError;
	};
}

interface Props {
	insuranceReport: InsuranceReport;
	onUpdate: () => void;
}

export const STATE_OPTIONS: SelectValue[] = [
	{
		label: 'base.insuranceReportState.unreported',
		value: InsuranceReportState.Unreported,
	},
	{
		label: 'base.insuranceReportState.error',
		value: InsuranceReportState.Error,
	},
	{
		label: 'base.insuranceReportState.reported',
		value: InsuranceReportState.Reported,
	},
	{
		label: 'base.insuranceReportState.cancelled',
		value: InsuranceReportState.Cancelled,
	},
	{
		label: 'base.insuranceReportState.rejected',
		value: InsuranceReportState.Rejected,
	},
	{
		label: 'base.insuranceReportState.partiallyAccepted',
		value: InsuranceReportState.PartiallyAccepted,
	},
	{
		label: 'base.insuranceReportState.irreparable',
		value: InsuranceReportState.Irreparable,
	},
];

export const STATE_ERROR_OPTIONS: SelectValue[] = [
	{
		label: 'base.insuranceReportStateError.ok',
		value: InsuranceReportStateError.Ok,
	},
	{
		label: 'base.insuranceReportStateError.error',
		value: InsuranceReportStateError.Error,
	},
];

const validationSchema = yup.object({
	reportedAt: yup
		.date()
		.required('validation.insuranceReportCreate.date')
		.typeError('validation.insuranceReportCreate.date'),
	doctor: yup.mixed().required('validation.insuranceReportCreate.doctor'),
	diagnosis: yup.mixed().required('validation.insuranceReportCreate.diagnosis'),
	operation: yup.mixed().required('validation.insuranceReportCreate.operation'),
	count: yup
		.number()
		.required('validation.insuranceReportCreate.count')
		.typeError('validation.insuranceReportCreate.countNumber'),
	points: yup
		.number()
		.required('validation.insuranceReportCreate.points')
		.typeError('validation.insuranceReportCreate.pointsNumber'),
	state: yup.mixed().required('validation.insuranceReportCreate.state'),
	stateError: yup
		.mixed()
		.required('validation.insuranceReportCreate.stateError'),
});

const InsuranceReportUpdate: React.FC<Props> = ({
	onUpdate,
	insuranceReport,
}) => {
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { handleSearchDiagnosisItem } = useNCZIReference();
	const { handleDateTimeValue, handleSelectValue } = useFilters();

	const prepareDefaultValues = async (values: InsuranceReport) => {
		let diagnosisValue: string | SelectValue | undefined = values.diagnosis!;

		if (typeof diagnosisValue === 'string') {
			const diagnosisItems = await handleSearchDiagnosisItem(
				diagnosisValue,
				true,
			);
			diagnosisValue = diagnosisItems.find(
				i => i.label.split(' ')?.[0] === diagnosisValue,
			);
		}

		const state = STATE_OPTIONS.find(item => item.value === values.state);
		const stateError = STATE_ERROR_OPTIONS.find(
			item => item.value === values.stateError,
		);

		return {
			diagnosis: diagnosisValue,
			operation: { value: values.operation?.id, points: 0 },
			points: values.points,
			count: values.count,
			birthNumber: values.patient?.profile?.birthNumber,
			reportedAt: values.reportedAt,
			state: state
				? {
						value: state?.value,
						label: t(state?.label),
				  }
				: undefined,
			stateError: stateError
				? {
						value: stateError?.value,
						label: t(stateError?.label),
				  }
				: undefined,
			doctor: {
				value: values.doctorId,
			},
		};
	};

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const operationValue = formMethods.watch('operation');

	useEffect(() => {
		if (
			operationValue?.points &&
			operationValue?.value !== insuranceReport.operation?.id
		) {
			formMethods.setValue('points', operationValue.points);
		}
	}, [operationValue]);

	useEffectAsync(async () => {
		const defaultValues = await prepareDefaultValues(insuranceReport);
		formMethods.reset(defaultValues);
	}, [insuranceReport]);

	const [updateInsuranceReport] = useInsuranceReportUpdateMutation();

	const handleSubmit = async (values: InsuranceReportUpdateValues) => {
		setIsLoading(true);
		const result = await updateInsuranceReport({
			input: {
				id: insuranceReport.id,
				diagnosis: values.diagnosis.label.split(' ')?.[0],
				operationId: values.operation.value,
				count: parseInt(values.count),
				points: parseInt(values.points),
				birthNumber: values.birthNumber,
				reportedAt: handleDateTimeValue(values.reportedAt),
				state: values.state.value,
				stateError: values.stateError.value,
				doctorId: values.doctor.value as string,
			},
		});
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.insuranceReportUpdate.successMessage'),
			});
			onUpdate();
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	if (!insuranceReport.insuranceId) {
		return <Text tx='component.insuranceReportCreate.missingDataError' />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<InputWrapper>
				<DateField
					label='component.insuranceReportCreate.date'
					name='reportedAt'
					isRequired
				/>
			</InputWrapper>
			<DoctorSelectField
				name='doctor'
				label='component.insuranceReportCreate.doctor'
				isRequired
				isWrapped
			/>
			<InputWrapper>
				<NCZIReferenceSearchSelectField
					label='component.insuranceReportCreate.diagnosis'
					name='diagnosis'
					oid={NCZI_OID.DIAGNOSIS}
					showCode
					isRequired
				/>
			</InputWrapper>
			<InsuranceOperationSelectField
				label='component.insuranceReportCreate.operation'
				name='operation'
				isRequired
				insuranceId={insuranceReport.insuranceId}
				isWrapped
			/>
			<InputWrapper>
				<NumberField
					label='component.insuranceReportCreate.count'
					name='count'
					isRequired
				/>
			</InputWrapper>
			<InputWrapper>
				<NumberField
					label='component.insuranceReportCreate.points'
					name='points'
					isRequired
				/>
			</InputWrapper>
			<InputField
				label='component.insuranceReportCreate.birthNumber'
				name='birthNumber'
				isRequired
				isWrapped
			/>
			<InputWrapper>
				<SelectField
					name='state'
					options={STATE_OPTIONS}
					label='component.insuranceReportCreate.state'
					portal
					isRequired
				/>
			</InputWrapper>
			<InputWrapper>
				<SelectField
					name='stateError'
					options={STATE_ERROR_OPTIONS}
					label='component.insuranceReportCreate.stateError'
					portal
					isRequired
				/>
			</InputWrapper>
			<SubmitButton onSubmit={handleSubmit} title='base.shared.edit' />
		</FormWrapper>
	);
};

export default InsuranceReportUpdate;
